import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useCandidateBySlug, useCandidateExperience, useCandidateAssociations } from '@/lib/api/hooks';
import { setSeo, resetSeo } from '@/lib/seo';
import { availabilityLabels, workTypeLabels } from '@/lib/validation/registration';
import BoringAvatar from '@/components/BoringAvatar';
import { VerifiedSeal } from '@/components/VerifiedSeal';
import './PrintableCV.css';

const PROFICIENCY_PCT: Record<string, number> = {
  Beginner: 35,
  Intermediate: 70,
  Expert: 100,
};

export function PrintableCV() {
  const { slug } = useParams<{ slug: string }>();
  const { data: envelope, isLoading } = useCandidateBySlug(slug ?? '');
  const c = envelope?.data;

  const { data: apiExperience } = useCandidateExperience(c?._id ?? '');
  const { data: membershipsEnvelope } = useCandidateAssociations(c?.legalEntityId ?? '');
  const memberships = membershipsEnvelope?.data ?? [];
  const isVerified = c?.isVerified ?? false;
  const experience = useMemo(() => {
    if (!apiExperience?.length) return [];
    return apiExperience.map((e: typeof apiExperience[number]) => ({
      company: e.companyName,
      title: e.jobTitle,
      description: e.description ?? '',
      startDate: e.startDate,
      endDate: e.isCurrent ? 'Present' : (e.endDate ?? ''),
      location: e.workLocation?.label ?? '',
    }));
  }, [apiExperience]);

  const groupedSkills = useMemo(() => {
    if (!c) return {};
    const groups: Record<string, typeof c.skills> = {};
    for (const s of c.skills ?? []) {
      const g = s.category ?? 'General';
      (groups[g] ??= []).push(s);
    }
    return groups;
  }, [c]);

  useEffect(() => {
    if (!c?.name) return;
    setSeo({
      title: `${c.name} — CV | BluePages`,
      description: `Printable CV for ${c.name} — ${c.headline}`,
      canonical: `${window.location.origin}/candidate/${c.slug}/print`,
    });
    return () => resetSeo();
  }, [c?.name, c?.slug, c?.headline]);

  if (isLoading || !c) {
    return (
      <div className="cv-loading">
        <div className="cv-spinner" />
        <p>Loading profile…</p>
      </div>
    );
  }

  const salary = c.desiredSalaryMin
    ? `R${c.desiredSalaryMin.toLocaleString()} – R${c.desiredSalaryMax?.toLocaleString()}`
    : null;

  const locations = c.desiredLocations?.length
    ? c.desiredLocations.map((d) => d.label).join(', ')
    : 'Flexible';

  return (
    <div className="cv-page">
      {/* Screen-only print action — hidden when printing */}
      <div className="cv-print-bar no-print">
        <button className="cv-print-btn" type="button" onClick={() => window.print()}>
          Print this CV
        </button>
      </div>

      {/* ── Header ── */}
      <header className="cv-header">
        <div className="cv-avatar">
          <BoringAvatar name={c.name} size={80} />
        </div>
        <div className="cv-header-text">
          <h1 className="cv-name">{c.name}</h1>
          <p className="cv-headline">{c.headline}</p>
          <div className="cv-meta">
            {c.availability && (
              <span className="cv-meta-item">
                <span className="cv-meta-dot" />
                {availabilityLabels[c.availability] ?? c.availability}
              </span>
            )}
            {c.workType && (
              <span className="cv-meta-item">
                <span className="cv-meta-dot" />
                {workTypeLabels[c.workType] ?? c.workType}
              </span>
            )}
            {salary && (
              <span className="cv-meta-item">
                <span className="cv-meta-dot" />
                {salary}
              </span>
            )}
            <span className="cv-meta-item">
              <span className="cv-meta-dot" />
              {locations}
            </span>
          </div>
        </div>
      </header>

      {/* ── Bio ── */}
      {c.bio && (
        <section className="cv-section">
          <h2 className="cv-section-title">About</h2>
          <p className="cv-body-text">{c.bio}</p>
        </section>
      )}

      {/* ── Skills ── */}
      <section className="cv-section">
        <h2 className="cv-section-title">Skills &amp; expertise</h2>
        {Object.entries(groupedSkills).map(([group, skills]) => (
          <div className="cv-skill-group" key={group}>
            <h3 className="cv-skill-group-title">{group}</h3>
            <div className="cv-skill-grid">
              {skills.map((s) => (
                <div className="cv-skill" key={s.name}>
                  <div className="cv-skill-name">{s.name}</div>
                  {s.proficiency && (
                    <div className="cv-skill-bar-track">
                      <div
                        className="cv-skill-bar-fill"
                        style={{ width: `${PROFICIENCY_PCT[s.proficiency] ?? 70}%` }}
                      />
                    </div>
                  )}
                  {s.proficiency && <span className="cv-skill-level">{s.proficiency}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* ── Experience ── */}
      {experience.length > 0 && (
        <section className="cv-section">
          <h2 className="cv-section-title">Work experience</h2>
          {experience.map((e, i) => (
            <div className="cv-exp" key={i}>
              <div className="cv-exp-top">
                <div>
                  <div className="cv-exp-title">{e.title}</div>
                  <div className="cv-exp-company">
                    {e.company}
                    {e.location ? ` · ${e.location}` : ''}
                  </div>
                </div>
                <div className="cv-exp-dates">
                  {e.startDate} – {e.endDate}
                </div>
              </div>
              {e.description && <p className="cv-exp-desc">{e.description}</p>}
            </div>
          ))}
        </section>
      )}

      {/* ── Professional Memberships ── */}
      {memberships.length > 0 && (
        <section className="cv-section">
          <h2 className="cv-section-title">Professional memberships</h2>
          {memberships.map((m) => (
            <div className="cv-membership" key={m._id}>
              <div className="cv-membership-top">
                <div className="cv-membership-name">{m.association.name}</div>
                <span className={`cv-membership-status cv-membership-status--${m.status?.toLowerCase() ?? 'unknown'}`}>
                  {m.status}
                </span>
              </div>
              <div className="cv-membership-detail">
                {m.membershipNumber}
                {m.membershipLevel ? ` · ${m.membershipLevel}` : ''}
              </div>
              {(m.issuedDate || m.expiryDate) && (
                <div className="cv-membership-dates">
                  {m.issuedDate && <span>Issued: {m.issuedDate}</span>}
                  {m.expiryDate && <span>Expires: {m.expiryDate}</span>}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* ── Locations ── */}
      {c.desiredLocations?.length && c.desiredLocations.length > 1 && (
        <section className="cv-section">
          <h2 className="cv-section-title">Preferred locations</h2>
          <div className="cv-locations">
            {c.desiredLocations.map((d) => (
              <span className="cv-location-chip" key={d.label}>
                {d.label}
                {d.radiusKm ? ` · within ${d.radiusKm} km` : ''}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── Footer branding ── */}
      <footer className="cv-footer">
        <div className="cv-footer-line" />
        <div className="cv-footer-content">
          <div className="cv-footer-left">
            <span className="cv-footer-logo">blue pages</span>
            <span className="cv-footer-text">
              Profile created on BluePages · <span className="cv-footer-url">bluepages.co.za</span>
            </span>
            {isVerified && (
              <span className="cv-footer-verified">
                <VerifiedSeal size={22} /> Identity verified
              </span>
            )}
          </div>
          <div className="cv-footer-qr">
            <QRCodeSVG
              value={`https://bluepages.co.za/candidate/${c.slug}`}
              size={56}
              bgColor="transparent"
              fgColor="#2563eb"
              level="M"
              includeMargin={false}
            />
            <span className="cv-footer-qr-label">View profile</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
